import { useEffect } from 'react';
import { useFormApi, useFormState } from 'informed';
import api from 'api';
import { breakpoints } from 'styles';
import { useDispatch, useReduxState } from 'hooks';
import {
  getCustomAttributesTypes,
  SPECIAL_CHARS_TO_ENCODE
} from 'lib/customAttributes';
import { Box } from 'components/common/box';
import {
  Checkbox,
  Code,
  FormGroup,
  LanguageVariants,
  NumberStepper,
  Select,
  Switch,
  Text
} from 'components/common/form';
import {
  UserAttributesConditionsButton,
  UserAttributesConditionsManager,
  UserAttributesRequirePassword
} from '.';

import type { FormApi } from 'informed';
import type { UserCustomAttribute } from 'shared-types';
import type { GetUserCustomAttributesRes } from 'shared-types/responses';

export type UserAttributesAttributeFormState = Pick<
  UserCustomAttribute,
  | 'appliesTo'
  | 'conditions'
  | 'description'
  | 'descriptionTranslations'
  | 'excludeFromExcelExport'
  | 'isEnabled'
  | 'isRequired'
  | 'key'
  | 'max'
  | 'min'
  | 'permissions'
  | 'showBeforeRegistration'
  | 'title'
  | 'titleTranslations'
  | 'type'
> & {
  validation: {
    errorMessage: UserCustomAttribute['validation']['errorMessage'];
    errorMessageTranslations: UserCustomAttribute['validation']['errorMessageTranslations'];
    matchList: string;
    matchListEnableEditing: boolean;
    matchRegex: string;
    requiredPasswords: {
      passwordIdentifier: string;
      passwordValue: string;
      title: string;
      titleTranslations: { [key: string]: string };
      valueMatchRegex: string;
    }[];
    validateFunction: string;
  };
};

interface Props {
  attribute?: UserCustomAttribute;
  userCustomAttributes: GetUserCustomAttributesRes['userCustomAttributes'];
}

const UserAttributesAttributeForm = ({
  attribute,
  userCustomAttributes
}: Props) => {
  const { dispatch } = useDispatch();
  const { messages, theme } = useReduxState();

  const formApi = useFormApi<FormApi<UserAttributesAttributeFormState>>();
  const formState = useFormState<UserAttributesAttributeFormState>();

  useEffect(() => {
    getRequiredPasswordValues();
  }, []);

  const getRequiredPasswordValues = async () => {
    try {
      if (!attribute || !attribute.validation?.requiredPasswords?.length) {
        return;
      }

      let index = 0;

      for (const requiredPassword of attribute.validation.requiredPasswords) {
        const apiKeyRequest = await api.get(
          `/admin/server-config/api-keys/${requiredPassword.passwordIdentifier}?unmask=true`
        );
        const passwordValue = apiKeyRequest.data.apiKey;
        formApi.setValue(
          `validation.requiredPasswords[${index++}].passwordValue`,
          passwordValue
        );
      }
    } catch (error) {
      await dispatch.handleErrorDispatch(error);
    }
  };

  const handleTitleChange = (value: string) => {
    if (!attribute) formApi.setValue('key', value);
  };

  const validateKey = async (value: string) => {
    const existingError = formState.errors?.key;
    if (existingError) return;

    const invalidChars = Object.values(SPECIAL_CHARS_TO_ENCODE);

    const containsInvalidChars = invalidChars.some((char) =>
      value.includes(char)
    );
    if (containsInvalidChars) {
      formApi.setError(
        'key',
        `Key contains invalid characters (${invalidChars.join(', ')})`
      );
      return;
    }

    try {
      await api.head(`/admin/user-custom-attributes?key=${value}`);
      formApi.setError('key', 'Key already in use');
    } catch (err) {
      return undefined;
    }
  };

  const validateRequired = (value) => {
    return !value?.toString().trim() ? messages.commonFieldRequired : undefined;
  };

  const validateKeyFormat = (value: string) => {
    return !value
      ? messages.commonFieldRequired
      : /^_.*\.edited$/.test(value)
      ? 'Key format invalid'
      : undefined;
  };

  const attributeTypes = getCustomAttributesTypes({ messages });

  const availableAttributes = {};
  userCustomAttributes
    .filter(
      (availableAttribute) =>
        !attribute || availableAttribute.id !== attribute.id
    )
    .forEach((availableAttribute) => {
      availableAttributes[availableAttribute.key] = availableAttribute;
    });

  const baseId = `user-attributes-attribute-form-${attribute?.id || 0}`;

  return (
    <div className="attribute-form">
      <div className="main">
        <Box>
          <FormGroup>
            <div className="row-md">
              <div className="col-1of2">
                <FormGroup label={messages.commonTitle}>
                  <Text
                    field="title"
                    onValueChange={handleTitleChange}
                    validate={validateRequired}
                    validateOnChange={true}
                  />

                  <LanguageVariants field="titleTranslations" type="text" />
                </FormGroup>
              </div>

              <div className="col-1of2">
                <FormGroup label={messages.attributesKey}>
                  <Text
                    disabled={Boolean(attribute)}
                    onValueChange={Boolean(attribute) ? undefined : validateKey}
                    field="key"
                    validate={validateKeyFormat}
                    validateOnChange={true}
                  />
                </FormGroup>
              </div>
            </div>
          </FormGroup>

          <FormGroup label={messages.commonDescription}>
            <Text field="description" />

            <LanguageVariants field="descriptionTranslations" type="text" />
          </FormGroup>
        </Box>

        <Box>
          <FormGroup label={messages.userAttributesFormApplies} maxWidth="100%">
            <div className="user-types">
              <Checkbox
                field="appliesTo.user"
                id={`${baseId}-target-users-user`}
                label={messages.commonUser}
                stopPropagation={true}
              />
              <Checkbox
                field="appliesTo.admin"
                id={`${baseId}-target-users-admin`}
                label={messages.usersAdmin}
                stopPropagation={true}
              />
              <Checkbox
                field="appliesTo.guest"
                id={`${baseId}-target-users-guest`}
                label={messages.usersGuest}
                stopPropagation={true}
              />
            </div>
          </FormGroup>

          <UserAttributesConditionsManager
            availableAttributes={availableAttributes}
            includeAuthenticationTypeCondition={true}
            includeEmailCondition={true}
            includeEmailDomainCondition={true}
            includeIsSelfRegisteredCondition={true}
          />
        </Box>

        <Box>
          <FormGroup label={messages.commonType} maxWidth="200px">
            <Select
              disabled={Boolean(attribute)}
              field="type"
              id={`${baseId}-type`}
              options={Object.keys(attributeTypes).map((type) => ({
                label: attributeTypes[type],
                value: type
              }))}
            />
          </FormGroup>

          {formState.values.type === 'String' ? (
            <>
              <FormGroup label={'Validation regex'} maxWidth="400px">
                <Text
                  field="validation.matchRegex"
                  placeholder="^\d+-[a-z]+$"
                  style={{ fontFamily: 'monospace' }}
                />
              </FormGroup>

              <FormGroup label={'Validation list'} maxWidth="400px">
                <Code
                  field="validation.matchList"
                  placeholder={'item1\nitem2\nitem3'}
                />

                <Checkbox
                  field="validation.matchListEnableEditing"
                  id={`${baseId}-validation-match-list-enable-editing`}
                  label={'Enable editing for admin or superadmin'}
                  stopPropagation={true}
                  style={{ marginTop: 5 }}
                />
              </FormGroup>

              <FormGroup label={'Validation function'} maxWidth="100%">
                <Code
                  field="validation.validateFunction"
                  placeholder={"return value?.startsWith('test');"}
                />
              </FormGroup>

              <FormGroup label={messages.attributesValidationErrorMessage}>
                <Text
                  field="validation.errorMessage"
                  placeholder={messages.commonFieldInvalid}
                />

                <LanguageVariants
                  field="validation.errorMessageTranslations"
                  type="text"
                />
              </FormGroup>
            </>
          ) : formState.values.type === 'Number' ? (
            <FormGroup style={{ marginTop: 10 }}>
              <NumberStepper field="min" id="min" stepValue={1} label={'Min'} />

              <NumberStepper
                field="max"
                id="max"
                label={'Max'}
                stepValue={1}
                style={{ marginTop: 10 }}
              />
            </FormGroup>
          ) : null}
        </Box>

        <UserAttributesRequirePassword
          initialRequiredPasswords={attribute?.validation?.requiredPasswords}
        />
      </div>

      <div className="side">
        <Box>
          <FormGroup label={messages.commonEnabled}>
            <Switch
              field="isEnabled"
              id={`${baseId}-is-enabled`}
              label={messages.commonEnabled}
            />
          </FormGroup>

          <FormGroup label={messages.commonRequired}>
            <div className="horizontal-container">
              <Checkbox
                field="isRequired.value"
                id={`${baseId}-is-required`}
                label={messages.commonRequired}
                stopPropagation={true}
              />
              <UserAttributesConditionsButton
                availableAttributes={availableAttributes}
                fieldPrefix="isRequired"
                includeAuthenticationTypeCondition={true}
                includeEmailCondition={true}
                includeIsSelfRegisteredCondition={true}
                key={`${baseId}-permissions-required-conditions`}
              />
            </div>
          </FormGroup>

          <FormGroup label={messages.commonSettings}>
            <Checkbox
              field="excludeFromExcelExport"
              id={`${baseId}-exclude-from-excel-export`}
              label={messages.userAttributesExcludeFromExcelExport}
              stopPropagation={true}
            />
          </FormGroup>

          <FormGroup label={messages.commonRegistration}>
            <Checkbox
              field="showBeforeRegistration"
              id={`${baseId}-show-before-registration`}
              label={messages.userAttributesShowBeforeRegistration}
              stopPropagation={true}
            />
          </FormGroup>
        </Box>

        <Box padding={0}>
          <div className="side-label">{messages.userAttributesFormVisible}</div>
          <div className="side-item">
            <Checkbox
              field="permissions.view.user.value"
              id={`${baseId}-permissions-view-user`}
              label={messages.commonUser}
              stopPropagation={true}
            />

            <UserAttributesConditionsButton
              availableAttributes={availableAttributes}
              fieldPrefix="permissions.view.user"
              includeAuthenticationTypeCondition={true}
              includeEmailCondition={true}
              includeEmailDomainCondition={true}
              includeIsSelfRegisteredCondition={true}
              key={`${baseId}-permissions-view-user-conditions`}
            />
          </div>
          <div className="side-item">
            <Checkbox
              field="permissions.view.admin.value"
              id={`${baseId}-permissions-view-admin`}
              label={messages.usersAdmin}
              stopPropagation={true}
            />

            <UserAttributesConditionsButton
              availableAttributes={availableAttributes}
              fieldPrefix="permissions.view.admin"
              includeAuthenticationTypeCondition={true}
              includeEmailCondition={true}
              includeEmailDomainCondition={true}
              includeIsSelfRegisteredCondition={true}
              key={`${baseId}-permissions-view-admin-conditions`}
            />
          </div>
          <div className="side-item">
            <Checkbox
              field="permissions.view.superadmin.value"
              id={`${baseId}-permissions-view-superadmin`}
              label={messages.usersSuperadmin}
              stopPropagation={true}
            />

            <UserAttributesConditionsButton
              availableAttributes={availableAttributes}
              fieldPrefix="permissions.view.superadmin"
              includeAuthenticationTypeCondition={true}
              includeEmailCondition={true}
              includeEmailDomainCondition={true}
              includeIsSelfRegisteredCondition={true}
              key={`${baseId}-permissions-view-superadmin-conditions`}
            />
          </div>
        </Box>

        <Box padding={0}>
          <div className="side-label">{messages.commonEditingRights}</div>
          <div className="side-item">
            <Checkbox
              field="permissions.edit.user.value"
              id={`${baseId}-permissions-edit-user`}
              label={messages.commonUser}
              stopPropagation={true}
            />

            <UserAttributesConditionsButton
              availableAttributes={availableAttributes}
              fieldPrefix="permissions.edit.user"
              includeAuthenticationTypeCondition={true}
              includeEmailCondition={true}
              includeEmailDomainCondition={true}
              includeIsSelfRegisteredCondition={true}
              key={`${baseId}-permissions-edit-user-conditions`}
            />
          </div>
          <div className="side-item">
            <Checkbox
              field="permissions.edit.admin.value"
              id={`${baseId}-permissions-edit-admin`}
              label={messages.usersAdmin}
              stopPropagation={true}
            />

            <UserAttributesConditionsButton
              availableAttributes={availableAttributes}
              fieldPrefix="permissions.edit.admin"
              includeAuthenticationTypeCondition={true}
              includeEmailCondition={true}
              includeEmailDomainCondition={true}
              includeIsSelfRegisteredCondition={true}
              key={`${baseId}-permissions-edit-admin-conditions`}
            />
          </div>
          <div className="side-item">
            <Checkbox
              field="permissions.edit.superadmin.value"
              id={`${baseId}-permissions-edit-superadmin`}
              label={messages.usersSuperadmin}
              stopPropagation={true}
            />

            <UserAttributesConditionsButton
              availableAttributes={availableAttributes}
              fieldPrefix="permissions.edit.superadmin"
              includeAuthenticationTypeCondition={true}
              includeEmailCondition={true}
              includeEmailDomainCondition={true}
              includeIsSelfRegisteredCondition={true}
              key={`${baseId}-permissions-edit-superadmin-conditions`}
            />
          </div>
        </Box>

        <Box padding={0}>
          <div className="side-label">{'Admin settings visible to'}</div>
          <div className="side-item">
            <Checkbox
              field="permissions.viewSettings.admin.value"
              id={`${baseId}-permissions-view-settings-admin`}
              label={messages.usersAdmin}
              stopPropagation={true}
            />

            <UserAttributesConditionsButton
              availableAttributes={availableAttributes}
              fieldPrefix="permissions.viewSettings.admin"
              includeAuthenticationTypeCondition={true}
              includeEmailCondition={true}
              includeEmailDomainCondition={true}
              includeIsSelfRegisteredCondition={true}
              key={`${baseId}-permissions-view-settings-admin-conditions`}
            />
          </div>
          <div className="side-item">
            <Checkbox
              field="permissions.viewSettings.superadmin.value"
              id={`${baseId}-permissions-view-settings-superadmin`}
              label={messages.usersSuperadmin}
              stopPropagation={true}
            />

            <UserAttributesConditionsButton
              availableAttributes={availableAttributes}
              fieldPrefix="permissions.viewSettings.superadmin"
              includeAuthenticationTypeCondition={true}
              includeEmailCondition={true}
              includeEmailDomainCondition={true}
              includeIsSelfRegisteredCondition={true}
              key={`${baseId}-permissions-view-settings-superadmin-conditions`}
            />
          </div>
        </Box>

        <Box padding={0}>
          <div className="side-label">{'Admin settings editing rights'}</div>
          <div className="side-item">
            <Checkbox
              field="permissions.editSettings.admin.value"
              id={`${baseId}-permissions-edit-settings-admin`}
              label={messages.usersAdmin}
              stopPropagation={true}
            />

            <UserAttributesConditionsButton
              availableAttributes={availableAttributes}
              fieldPrefix="permissions.editSettings.admin"
              includeAuthenticationTypeCondition={true}
              includeEmailCondition={true}
              includeEmailDomainCondition={true}
              includeIsSelfRegisteredCondition={true}
              key={`${baseId}-permissions-edit-settings-admin-conditions`}
            />
          </div>
          <div className="side-item">
            <Checkbox
              field="permissions.editSettings.superadmin.value"
              id={`${baseId}-permissions-edit-settings-superadmin`}
              label={messages.usersSuperadmin}
              stopPropagation={true}
            />

            <UserAttributesConditionsButton
              availableAttributes={availableAttributes}
              fieldPrefix="permissions.editSettings.superadmin"
              includeAuthenticationTypeCondition={true}
              includeEmailCondition={true}
              includeEmailDomainCondition={true}
              includeIsSelfRegisteredCondition={true}
              key={`${baseId}-permissions-edit-settings-superadmin-conditions`}
            />
          </div>
        </Box>
      </div>

      <style jsx>{`
        .attribute-form {
          background-color: ${theme.lightGreyLighter};
          display: flex;
          flex-direction: column;
          gap: 15px;
          padding: 15px;
        }
        .horizontal-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .main {
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          gap: 15px;
        }
        .user-types {
          display: flex;
          flex-wrap: wrap;
          gap: 10px 30px;
        }
        .side {
          display: flex;
          flex-basis: calc(33.33333333% - 15px);
          flex-direction: column;
          flex-shrink: 0;
          gap: 15px;
        }
        .side-label {
          padding: 10px 15px;
        }
        .side-item {
          align-items: center;
          border-top: 1px solid ${theme.lightGrey};
          display: flex;
          gap: 10px;
          justify-content: space-between;
          padding: 10px 15px;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .attribute-form {
            flex-direction: row;
          }
        }
      `}</style>
    </div>
  );
};

export default UserAttributesAttributeForm;
